import React, {memo, useEffect, useRef, useState} from "react";
import {Edge, Node} from "@xyflow/react";
import {Button} from "@/components/ui/Button";
import {Copy, Info, X} from "lucide-react";
import {useToast} from "@/hooks/useToast";
import {Icon} from "@/components/ui/icon";
import {generatePrompt} from "@/utils/index";
import ReactMarkdown from "react-markdown";
import {Alert} from "@/components/ui/Alert";

interface PromptDrawerProps {
  nodes: Node[];
  edges: Edge[];
  setShowPrompt: (showPrompt: boolean) => void;
}

const sections = [
  {id: "general-behaviour", title: "General Behaviour"},
  {id: "background-information", title: "Background Info"},
  {id: "frequently-asked-questions", title: "FAQs"},
  {id: "special-cases-and-objections", title: "Special Cases"},
  {id: "conversation-steps", title: "Conversation Steps"},
  {id: "general-instructions", title: "General Instructions"},
];

const PromptDrawer: React.FC<PromptDrawerProps> = ({
  nodes,
  edges,
  setShowPrompt,
}) => {
  const {toast} = useToast();
  const [activeSection, setActiveSection] = useState("");
  const [highlightedSection, setHighlightedSection] = useState("");

  const contentRef = useRef(null);
  const prompt = generatePrompt(nodes, edges);

  const scrollToSection = id => {
    const element = window.__flowBuilderIframeDoc.querySelector(
      `[data-id='${id}']`,
    ) as HTMLElement;
    if (element) {
      const parentContainer =
        window.__flowBuilderIframeDoc.querySelector(".markdown-content");

      if (parentContainer) {
        const elementTop = element.getBoundingClientRect().top;
        const parentTop = parentContainer.getBoundingClientRect().top;
        const offset = elementTop - parentTop + parentContainer.scrollTop - 10;

        parentContainer.scrollTo({top: offset, behavior: "smooth"});
        setActiveSection(id);
      }

      setHighlightedSection(id);
    }
  };

  useEffect(() => {
    if (sections.length > 0) {
      setActiveSection(sections[0].id);
    }
  }, []);

  useEffect(() => {
    if (highlightedSection) {
      const element = window.__flowBuilderIframeDoc.querySelector(
        `[data-id='${highlightedSection}']`,
      );
      if (element) {
        element.classList.add("animate-highlight-text");

        setTimeout(() => {
          element.classList.remove("animate-highlight-text");
        }, 1000);
      }
    }
  }, [highlightedSection]);

  return (
    <div className="drawer h-full bg-white border rounded-xl flex flex-col overflow-hidden">
      <div className="flex max-h-full border-b px-5 py-3 gap-2 items-center">
        <h2 className="text-lg font-semibold text-ellipsis overflow-hidden whitespace-nowrap">
          View Final Prompt
        </h2>
        <Button
          className="ml-auto"
          variant="outline"
          onClick={() => {
            navigator.clipboard.writeText(prompt);
            toast({
              icon: (
                <Icon
                  size="tiny"
                  variant="rounded"
                  className="bg-green-500 border-0 shadow-toast-icon-green mr-3"
                >
                  <Copy className="text-slate-800 size-2.5 font-bold" />
                </Icon>
              ),
              title: "Prompt copied to clipboard",
            });
          }}
        >
          <Copy className="size-4.5 text-primary-accent" />
          Copy Prompt
        </Button>
        <Button
          className="[&_svg]:size-4.5 px-3"
          variant="ghost"
          onClick={() => setShowPrompt(false)}
        >
          <X />
        </Button>
      </div>
      <Alert
        variant="info"
        className="flex items-center border-0 border-b rounded-none"
      >
        <Info className="h-4 w-4" />
        <b className="mr-1">Want to test your Assistant in action?</b> Close the
        full screen mode to access the test calls.
      </Alert>
      <div className="flex gap-5 flex-1 overflow-hidden">
        <div className="flex flex-col gap-2 border-r p-5 bg-slate-50">
          {sections.map(section => (
            <Button
              key={section.id}
              variant={activeSection === section.id ? "primary" : "ghost"}
              onClick={() => scrollToSection(section.id)}
              className="justify-start"
              disabled={
                contentRef.current?.querySelector(`[data-id='${section.id}']`)
                  ? false
                  : true
              }
            >
              {section.title}
            </Button>
          ))}
        </div>

        {/* Markdown Content */}
        <div
          ref={contentRef}
          className="w-full overflow-auto flex-grow p-5 markdown-content"
        >
          <ReactMarkdown
            components={{
              h2: ({node, children}) => {
                const id = children
                  .toString()
                  .toLowerCase()
                  .replace(/\s+/g, "-");
                return (
                  <h2
                    data-id={id}
                    className="scroll-mt-20 px-2 py-1 rounded-md -ml-2"
                  >
                    {children}
                  </h2>
                );
              },
              h3: ({node, children}) => {
                const id = children
                  .toString()
                  .toLowerCase()
                  .replace(/\s+/g, "-");
                return (
                  <h3
                    data-id={id}
                    className="scroll-mt-20 px-2 py-1 rounded-md -ml-2"
                  >
                    {children}
                  </h3>
                );
              },
            }}
          >
            {prompt}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
};

export default memo(PromptDrawer);
